import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismBadge } from '@mylighthouse/prism-ember';
import { t, tKey } from 'ember-intl';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import {
  RI_SECTIONS,
  type RiSection,
} from 'lumen/utils/constants/ri-constant-values';

const DATA_ORIGIN_LABELS = {
  RESERVATION: tKey('revenue_insight.data_origin.reservation'),
  SUMMARY: tKey('revenue_insight.data_origin.summary'),
  SUMMARY_VS_RESERVATION: tKey(
    'revenue_insight.data_origin.summary_vs_reservation',
  ),
};

interface HotelAndMarketRevenueInsightDataOriginBadgeSignature {
  Args: {
    section: RiSection;
  };
}

export default class HotelAndMarketRevenueInsightDataOriginBadge extends Component<HotelAndMarketRevenueInsightDataOriginBadgeSignature> {
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dataOriginLabel() {
    if (
      this.riSelections.isCancellationsOnlyView ||
      this.args.section === RI_SECTIONS.PICKUP ||
      this.args.section === RI_SECTIONS.SEGMENTATION
    ) {
      return DATA_ORIGIN_LABELS.RESERVATION;
    }

    if (this.riSelections.isKPIsAndStayDateSummaryData) {
      return DATA_ORIGIN_LABELS.SUMMARY;
    }

    if (this.riSelections.isKPIsAndStayDateSummaryVSReservationData) {
      // Target always uses reservation data to spread the data across the entire period
      // Therefore it's redundant to always show "vs Reservation" in the target view
      if (this.riSelections.isTargetActive) {
        return DATA_ORIGIN_LABELS.SUMMARY;
      }

      return DATA_ORIGIN_LABELS.SUMMARY_VS_RESERVATION;
    }

    return DATA_ORIGIN_LABELS.RESERVATION;
  }

  get dataOriginTranslation(): string {
    return this.intl.t(this.dataOriginLabel);
  }

  get shouldShowTooltip(): boolean {
    return this.dataOriginLabel === DATA_ORIGIN_LABELS.SUMMARY_VS_RESERVATION;
  }

  <template>
    <PrismBadge>
      {{this.dataOriginTranslation}}

      {{#if this.shouldShowTooltip}}
        <GeneralIconWithTooltip
          @side="top"
          @tooltipContent={{t
            "revenue_insight.data_origin.summary_vs_reservation.info"
          }}
        />
      {{/if}}
    </PrismBadge>
  </template>
}
